import { faTimes, faWindowClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Form } from "@themesberg/react-bootstrap";
import React, { useEffect, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { numberFormatter } from "../../libs/numberFormatter";
import PriceField from "../../components/inputs/PriceField";
import { useSelector } from "react-redux";
import { selectProducts } from "../../store/reducers/products";
import AutoCompleteProductField from "../../components/inputs/AutoCompleteProductField";
import { Field } from "formik";

function InvoiceLine({
  rowId,
  productLineField,
  deleteInvoiceLine,
  setFieldValue,
  errors,
  touched,
  values,
  showDeleteButton,
}) {
  const { t } = useTranslation();

  const [isPricePrefilledFromProduct, setIsPricePrefilledFromProduct] = useState(false);
  const products = useSelector(selectProducts);

  useEffect(() => {
    setFieldValue(`${productLineField}.${rowId}.description`, values.product?.label ?? "");

    if (!values.product?.id) {
      setIsPricePrefilledFromProduct(false);
      return;
    }

    setFieldValue(`${productLineField}.${rowId}.product_id`, values.product?.id);
    setFieldValue(`${productLineField}.${rowId}.price`, values.product?.price);
    setIsPricePrefilledFromProduct(true);
  }, [values]);

  useEffect(() => {
    if (values?.price !== undefined && values.quantity !== undefined) {
      const totalAmountExclVat = (values.price * values.quantity) / 100;
      setFieldValue(`${productLineField}.${rowId}.amountExclVat_field`, totalAmountExclVat);
    }
  }, [values.price, values.quantity, rowId, setFieldValue]);

  return (
    <>
      <tr>
        <th>
          <Form.Group style={{ width: "200px" }}>
            <AutoCompleteProductField
              name={`${productLineField}.${rowId}.product`}
              products={products}
            />
            {errors?.[productLineField] && touched?.[productLineField] ? (
              <div className="validation-error">
                {t(errors?.[productLineField][rowId]?.product)}
              </div>
            ) : null}
          </Form.Group>
        </th>
        <th>
          <Form.Group>
            <Field
              as={Form.Control}
              type="number"
              name={`${productLineField}.${rowId}.quantity`}
              className="text-center"
              autoComplete="off"
              placeholder={t("Quantity")}
            />
            {errors?.[productLineField] && touched?.[productLineField] ? (
              <div className="validation-error">
                {t(errors?.[productLineField][rowId]?.quantity)}
              </div>
            ) : null}
          </Form.Group>
        </th>
        <th>
          <Form.Group>
            <div className="d-flex align-items-center">
              <div className="d-flex flex-column">
                <PriceField
                  name={`${productLineField}.${rowId}.price`}
                  disabled={isPricePrefilledFromProduct}
                  className="text-center"
                  autoComplete="off"
                />
              </div>
              {isPricePrefilledFromProduct && (
                <OverlayTrigger
                  placement="right"
                  delay={{ show: 250, hide: 400 }}
                  overlay={(props) => (
                    <Tooltip id="button-tooltip" {...props}>
                      {t(
                        "This invoice line was pre-filled using an existing product. The product is still attached to this invoice line. This will affect the product statistics. Clicking the cross will detach the product"
                      )}
                    </Tooltip>
                  )}
                >
                  <span
                    onClick={() => {
                      setIsPricePrefilledFromProduct(false);
                      setFieldValue(`${productLineField}.${rowId}.product.id`, null);
                    }}
                    className="p-1"
                  >
                    <FontAwesomeIcon icon={faWindowClose} />
                  </span>
                </OverlayTrigger>
              )}
            </div>
            {errors?.[productLineField] && touched?.[productLineField] && (
              <div className="validation-error">
                {t(errors?.[productLineField][rowId]?.price)}
              </div>
            )}
          </Form.Group>
        </th>
        <th>
          <Form.Group>
            <div className="d-flex align-items-center">
              <Field name={`${productLineField}.${rowId}.vat_percentage`}>
                {({ field }) => (
                  <Form.Select
                    {...field}
                    disabled={isPricePrefilledFromProduct}
                    value={field.value ?? ""}
                    onChange={(e) => {
                      // Convert string to number for Formik
                      const value = e.target.value === "" ? "" : Number(e.target.value);
                      field.onChange({
                        target: {
                          name: field.name,
                          value: value
                        }
                      });
                    }}
                  >
                    <option value="" disabled>
                      {t("Select vat percentage")}
                    </option>
                    <option value={0}>{t("VAT 0%")}</option>
                    <option value={6}>{t("VAT low 6%")}</option>
                    <option value={9}>{t("VAT low 9%")}</option>
                    <option value={21}>{t("VAT high 21%")}</option>
                    <option value={1}>{t("VAT shifted 0%")}</option>
                    <option value={2}>{t("VAT exempt 0%")}</option>
                  </Form.Select>
                )}
              </Field>
              {isPricePrefilledFromProduct && (
                <OverlayTrigger
                  placement="right"
                  delay={{ show: 250, hide: 400 }}
                  overlay={(props) => (
                    <Tooltip id="button-tooltip" {...props}>
                      {t(
                        "This invoice line was pre-filled using an existing product. The product is still attached to this invoice line. This will affect the product statistics. Clicking the cross will detach the product"
                      )}
                    </Tooltip>
                  )}
                >
                  <span
                    onClick={() => {
                      setIsPricePrefilledFromProduct(false);
                      setFieldValue(`${productLineField}.${rowId}.product.id`, null);
                    }}
                    className="p-1"
                  >
                    <FontAwesomeIcon icon={faWindowClose} />
                  </span>
                </OverlayTrigger>
              )}
            </div>
            {errors?.[productLineField] && touched?.[productLineField] ? (
              <div className="validation-error">
                {t(errors?.[productLineField][rowId]?.vat_percentage)}
              </div>
            ) : null}
          </Form.Group>
        </th>
        <th>
          <Form.Group>
            <Form.Control
              type="text"
              className="text-center"
              name={`${productLineField}.${rowId}.amountExclVat_field`}
              readOnly
              value={numberFormatter.format(values.amountExclVat_field) || ""}
              placeholder={t("amountExclVat")}
            />
            {errors?.[productLineField] && touched?.[productLineField] ? (
              <div className="validation-error">
                {t(errors?.[productLineField][rowId]?.amountExclVat_field)}
              </div>
            ) : null}
          </Form.Group>
        </th>
        <th>
          {showDeleteButton && (
            <Form.Group>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => deleteInvoiceLine(rowId)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </Form.Group>
          )}
        </th>
      </tr>
    </>
  );
}

export default InvoiceLine;
